import { useCallback, useState } from 'react';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import useUserStore from '@/stores/UserStore';
import { AlertOctagon, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { isMergedChannel } from '@/lib/utils';
import { updateContactStatus } from '@/hooks/contact';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { AvatarProfile } from '@/components/inbox/contact-card/AvatarProfile';

import CommentThread from './CommentThread';
import ContactOwner from './ContactOwner';
import ContactTags from './ContactTag';
import TipTapEditor from './Notes';

const ContactDetails = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const clientId = useUserStore((state) => state.clientId);
  const {
    fullName,
    profilePictureUrl,
    igUsername,
    plan,
    contactId,
    channel,
    ai_notes,
    followers_count,
    following_count,
    verified,
    bio,
    external_url,
    status,
    setContactStatus,
    linkedin_profile_link,
    phone,
    email,
  } = useContactStore(
    useShallow((state) => ({
      fullName: state.fullname,
      status: state.status,
      profilePictureUrl: state.profile_picture_url,
      igUsername: state.ig_username,
      ai_notes: state.ai_notes,
      plan: state.plan,
      contactId: state._id,
      channel: state.channel,
      followers_count: state.followers_count,
      following_count: state.following_count,
      verified: state.verified,
      bio: state.bio,
      external_url: state.external_url,
      setContactStatus: state.setContactStatus,
      linkedin_profile_link: state.linkedin_profile_link,
      phone: state.phone,
      email: state.email,
    }))
  );

  const {
    removeConversation,
    decreaseConversationCount,
    increaseConversationCount,
    selectedConversationStatus,
  } = useInboxStore(
    useShallow((state) => ({
      removeConversation: state.removeConversation,
      decreaseConversationCount: state.decreaseConversationCount,
      increaseConversationCount: state.increaseConversationCount,
      selectedConversationStatus: state.selectedConversationStatus,
    }))
  );

  const updateStatus = useCallback(
    async (newStatus) => {
      try {
        setLoading(true);

        const response = await updateContactStatus(
          contactId,
          newStatus,
          clientId
        );
        setContactStatus(newStatus);

        if (selectedConversationStatus.value !== 'all') {
          switch (selectedConversationStatus.value) {
            // If the contact status is changed to the selectedConversationStatus
            case newStatus:
              increaseConversationCount();
              break;

            // If the contact status is changed to the opposite of selectedConversationStatus
            default:
              removeConversation(contactId);
              decreaseConversationCount();
              navigate('/inbox', {
                replace: true,
              });
              break;
          }
        }
      } catch (error) {
        toast.error('Unable to update contact status');
      }
      setLoading(false);
    },
    [
      contactId,
      setContactStatus,
      removeConversation,
      decreaseConversationCount,
      increaseConversationCount,
      selectedConversationStatus,
      navigate,
      clientId,
    ]
  );

  if (!contactId) return null;

  return (
    <div className='flex w-full flex-col space-y-4 overflow-hidden'>
      <div className='flex flex-row items-start justify-start space-x-2 border-b border-borderColor px-4 py-3'>
        <div className='flex flex-col items-center justify-start gap-2'>
          <div>
            {channel === 'sms' || isMergedChannel(channel) ? (
              <div className='relative h-20 w-20 overflow-hidden rounded-full'>
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    className='h-full w-full object-cover'
                  />
                ) : fullName ? (
                  <div className='absolute inset-0 flex items-center justify-center rounded-full bg-avatar text-2xl text-primary'>
                    {fullName?.charAt(0)?.toUpperCase() || ''}
                  </div>
                ) : (
                  <img
                    src='https://files.sbccrm.com/sbccrm/sbccrm/contacts/pics/no-pic.jpg'
                    className='h-full w-full object-cover'
                  />
                )}
              </div>
            ) : (
              <Avatar className='h-20 w-20'>
                <AvatarImage
                  src={
                    profilePictureUrl
                      ? profilePictureUrl
                      : 'https://files.sbccrm.com/sbccrm/sbccrm/contacts/pics/no-pic.jpg'
                  }
                />
                <AvatarFallback>
                  {fullName?.charAt(0)?.toUpperCase() || ''}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
          {loading ? (
            <div className='flex h-12 w-12 items-center justify-center'>
              <Loader2 className='h-4 w-4 animate-spin text-gray-500' />
            </div>
          ) : status && (status === 'open' || status === 'close') ? (
            <Button
              variant='link'
              size='xs'
              className='gap-1 text-xs'
              onClick={() => updateStatus('spam')}
            >
              <AlertOctagon className='mr-[2px] h-4 w-4' />
              Mark as Spam
            </Button>
          ) : status === 'spam' ? (
            <Button
              variant='link'
              size='xs'
              className='gap-1 text-xs'
              onClick={() => updateStatus('open')}
            >
              <CheckCircle className='mr-[2px] h-4 w-4' />
              Not Spam
            </Button>
          ) : null}
        </div>

        <div className='flex w-64 flex-col items-start py-2'>
          <AvatarProfile
            name={fullName}
            username={igUsername}
            channel={channel}
            followers={followers_count || '0'}
            following={following_count || '0'}
            isVerified={verified}
            bio={bio}
            plan={plan}
            phone={phone}
            email={email}
            external_url={external_url}
            linkedin_profile_link={linkedin_profile_link}
          />
        </div>
      </div>

      <ContactTags />

      <ContactOwner />

      {channel === 'sms' || isMergedChannel(channel) ? null : <CommentThread />}

      <div className='space-y-2 border-b border-borderColor px-4'>
        <div className='text-sm font-medium'>AI Notes</div>
        <div className='space-y-2'>
          <div>
            <AINotes notes={ai_notes} />
          </div>
          <div>
            <TipTapEditor />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetails;

const AINotes = ({ notes }) => {
  return (
    notes && (
      <div className='relative rounded-full p-2'>
        <p className='relative z-10 whitespace-pre-line text-[0.85rem] leading-[1.1rem]'>
          {notes}
        </p>
        <div className='mask-gradient absolute inset-0 rounded-md bg-gradient-to-r from-purple-600 to-yellow-500 p-[2px]' />
      </div>
    )
  );
};
